import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  ChevronRight,
  Search,
  Sparkles,
  Users,
  X,
} from "lucide-react";
import { AppDispatch, RootState } from "../../../../store";
import { getDailyCommission } from "../../../../Redux/referral/slice";
import Pagenation from "../../../../ecommerce/globalcomponents/pagenation/Pagenation";
import DateRangePicker from "../../../../ecommerce/globalcomponents/DateRangePicker";
import DailyCommissonTable from "./DailyCommissonTable";
import { formatEarningsCurrency } from "../earningsAdapters";
import "./DailyIncentives.css";

const DailyCommissons = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { dailyCommission } = useSelector(
    (state: RootState) => state.referral,
  );

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (dateRange.from && !dateRange.to) return;

    dispatch(
      getDailyCommission({
        page,
        limit,
        status: "ALL",
        sortOrder: "DESC",
        search: debouncedSearch || undefined,
        startDate: dateRange.from || undefined,
        endDate: dateRange.to || undefined,
      }),
    );
  }, [dispatch, page, limit, debouncedSearch, dateRange.from, dateRange.to]);

  const records = dailyCommission.data || [];
  const totalRecords =
    dailyCommission.pagination?.total ?? records.length;

  return (
    <section className="daily-ref-page">
      <div className="daily-ref-shell">
        <header className="daily-ref-hero">
          <div className="daily-ref-hero-left">
            <div className="daily-ref-hero-icon">
              <Sparkles size={22} />
            </div>
            <div className="daily-ref-hero-copy">
              <span className="daily-ref-eyebrow">MY EARNINGS</span>
              <h1>Daily Incentives List</h1>
              <p>
                Track monthly contributor participation and daily incentive
                earnings.
              </p>
            </div>
          </div>
          <span className="daily-ref-count-pill">
            {totalRecords} periods
          </span>
        </header>

        <div className="daily-ref-toolbar">
          <select
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
            aria-label="Entries per page"
          >
            {[10, 25, 50, 75, 100].map((val) => (
              <option value={val} key={val}>
                {val} Entries
              </option>
            ))}
          </select>

          <div className="daily-ref-date">
            <DateRangePicker
              value={dateRange}
              onChange={(v) => {
                setDateRange(v);
                setPage(1);
              }}
            />
          </div>

          <div className="daily-ref-search">
            <Search size={17} />
            <input
              type="search"
              placeholder="Search incentives..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
            {search && (
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => setSearch("")}
              >
                <X size={15} />
              </button>
            )}
          </div>
        </div>

        <DailyCommissonTable data={records} />

        <div className="daily-ref-mobile-list">
          {records.map((item) => (
            <article key={item.sNo} className="daily-ref-mobile-card">
              <div className="daily-ref-mobile-header">
                <span className="daily-ref-mobile-index">{item.sNo}</span>
                <div className="daily-ref-mobile-title">
                  <span>MONTH - YEAR</span>
                  <strong>{item.monthYear}</strong>
                </div>
                <div className="daily-ref-mobile-amount">
                  <span>EARNED</span>
                  <strong>
                    {formatEarningsCurrency(item.netAmount ?? item.receivedIncentives)}
                  </strong>
                  {item.tdsAmount != null && (
                    <small>
                      TDS {formatEarningsCurrency(item.tdsAmount)}
                      {item.tdsIsEstimated ? " (est)" : ""}
                    </small>
                  )}
                </div>
              </div>
              <Link
                className="daily-ref-mobile-contributor-row"
                to={`/DailyCommissoinDetails?date=${item.monthYear}&code=${item.monthYearCode}`}
              >
                <span className="daily-ref-mobile-contributor-icon">
                  <Users size={17} />
                </span>
                <span className="daily-ref-mobile-contributor-copy">
                  <strong>{item.contributors} Contributors</strong>
                  <small>
                    View contributor-wise daily incentive details
                  </small>
                </span>
                <ChevronRight size={18} />
              </Link>
            </article>
          ))}
        </div>

        {records.length === 0 && (
          <div className="daily-ref-empty">No incentive records found.</div>
        )}

        <Pagenation
          currentPage={page}
          totalPages={dailyCommission.pagination?.totalPages || 0}
          onPageChange={setPage}
        />
      </div>
    </section>
  );
};

export default DailyCommissons;
